import React from 'react'
import { useParams } from 'react-router-dom'
import log from 'loglevel'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { LinkIcon } from 'src/components/Icons/FileType'
import { ShowPageSectionContainer } from 'src/components/PageLayout/ShowPage'
import { ProgressMeterWithText } from 'src/components/Reusable/ProgressMeter/ProgressMeter'
import { EroDarkSVG } from 'src/components/Icons/Brand'
import {
  PrimaryButton,
  StandardButtonSize,
} from 'src/components/Reusable/Buttons'
import { DisplayCost } from 'src/components/Reusable/Cost/Cost.components'
import { InfoSection } from 'src/components/Reusable/IndexPage/InfoSection'
import { HUBSPOT } from 'src/Utils/hubspot/hubspot.utils'
import { IndexPageActions } from 'src/components/PageLayout/IndexPage/IndexPageActions'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { useInAudit } from 'src/context/AuditContext'
import { useAuditBotModal } from 'src/components/ModalForms/Reusable/SelectControls/AuditScanModal.hooks'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  IconContainer,
  IconProgressRing,
  IconProgressRingCircle,
} from '../GRCDashboards/Compliance/ChecklistWidget/Card/Card.styles'
import {
  AuditPrecheckReadyContainer,
  AuditRunHeader,
  AuditScanInfoContainer,
  AuditbotSectionHeader,
  EmptyTab,
} from './AuditBot.styles'
import {
  AUDITBOT_SECTION_CONFIG,
  AuditBotCost,
  AuditbotCostTypes,
} from './AuditBot.constants'
import {
  AuditBotShowSectionExplanation,
  AuditBotShowSectionSummary,
  AuditBotStyledHeaderButton,
} from './show/AuditBotShowPage.styles'
import { CheckWithEroButton } from './reusable/CheckWithEroButton'
import { DateRange, useStartAuditRun } from './AuditBot.hooks'

export const AuditRunTitle = ({
  modelId,
  text,
  route,
  startTime,
  promptOverrideSourceName,
}: {
  modelId: string
  text: string
  startTime?: string
  route: string
  promptOverrideSourceName?: string
}): JSX.Element => {
  const { pageContext } = useParams()

  return (
    <AuditRunHeader>
      {startTime && <span>{startTime}</span>}
      <span>{text}</span>
      {promptOverrideSourceName && (
        <span title="Prompt override source">[{promptOverrideSourceName}]</span>
      )}
      <a
        href={`/${pageContext}/${route}/${encodeURIComponent(modelId)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        <LinkIcon />
      </a>
    </AuditRunHeader>
  )
}

export const EmptyControlAuditRuns = ({
  controlId,
  currentAudit,
  customText,
}: {
  controlId: string
  currentAudit?: AuditRecord
  customText?: string
}): JSX.Element => (
  <>
    <AuditScanInfoContainer>
      <InfoSection
        text="Control Test is powered by Trustero AI."
        link={HUBSPOT.AUDIT_SCAN_FAQ}
        isExternalLink
      />
    </AuditScanInfoContainer>
    <EmptyTab>
      <br />
      <EroDarkSVG />
      <p>{customText || 'This control has not been tested.'}</p>
      <CheckWithEroButton controlId={controlId} currentAudit={currentAudit} />
    </EmptyTab>
  </>
)

export const AuditBotShowSummarySection = ({
  summary,
}: {
  summary: AUDITBOT_SECTION_CONFIG
}): JSX.Element => {
  return (
    <ShowPageSectionContainer>
      <AuditbotSectionHeader>SUMMARY</AuditbotSectionHeader>
      <AuditBotShowSectionSummary>
        {summary.summary || summary.summaryText}
      </AuditBotShowSectionSummary>
      <AuditBotShowSectionExplanation>
        {summary.explanation}
      </AuditBotShowSectionExplanation>
    </ShowPageSectionContainer>
  )
}
export const AuditScanProgress = ({
  progress,
  progressMax,
}: {
  progress: number
  progressMax: number
}): JSX.Element => {
  return (
    <ProgressMeterWithText
      progress={progress}
      progressMax={progressMax}
      noun="control"
      verb="Testing"
      description="These tests give each control close scrutiny, reviewing evidence and
    checking each control with a number of appropriate tests."
    />
  )
}

export const ScanAgainHeaderButton = ({
  auditId,
  range,
  controlIds,
}: {
  auditId?: string
  range?: DateRange
  controlIds: string[]
}): JSX.Element => {
  const canStartAuditRun = useCanPerformAction(CAPABILITY.CAN_START_AUDIT_SCANS)
  const startAuditRun = useStartAuditRun()
  const startFunc = async () => {
    try {
      await startAuditRun(auditId, range, controlIds)
    } catch (error) {
      log.error(`Error starting audit run for auditId ${auditId}`, error)
    }
  }
  return (
    <AuditBotStyledHeaderButton
      size={StandardButtonSize.MEDIUM}
      onClick={startFunc}
      text="Retest Controls"
      isDisabled={!canStartAuditRun}
      disabledReason="You do not have permission to test controls."
    />
  )
}

export const AuditSectionReady = ({
  ready,
  total,
  icon,
  content,
}: {
  ready: number
  total: number
  icon?: JSX.Element
  content: string | JSX.Element
}): JSX.Element => {
  const percentage = ready / (total || 1)

  return (
    <AuditPrecheckReadyContainer>
      <IconContainer>
        {icon}
        <IconProgressRing>
          <IconProgressRingCircle progress={percentage} />
        </IconProgressRing>
      </IconContainer>
      <h5>{content}</h5>
    </AuditPrecheckReadyContainer>
  )
}

export const ControlTestCost = ({
  costPerControl = AuditBotCost.ControlTest,
  controlQuantity = 0,
  type,
  style,
  onClick,
}: {
  costPerControl: AuditBotCost
  controlQuantity: number
  type?: AuditbotCostTypes
  style?: React.CSSProperties
  onClick?: () => void
}): JSX.Element => {
  const msg = `Cost: ${controlQuantity * costPerControl || '--'} control test${
    controlQuantity !== 1 ? 's' : ''
  }`

  return (
    <DisplayCost
      message={msg}
      isLarge={type === AuditbotCostTypes.Label}
      style={style}
      onClick={onClick}
    />
  )
}

export const AuditBotIndexPageActions = (): JSX.Element => {
  const canStartAuditRun = useCanPerformAction(CAPABILITY.CAN_START_AUDIT_SCANS)
  const { auditId } = useInAudit()
  const openAuditBotModal = useAuditBotModal()
  const { openModal } = useModalState()

  return (
    <IndexPageActions
      description="Test controls ensuring operating procedures are met with Trustero AI."
      learnMoreUrl={HUBSPOT.AUDIT_SCAN_FAQ}
      buttonGroup={
        <FlexColumn align={FlexAlign.FLEX_END} gap={8}>
          <PrimaryButton
            size={StandardButtonSize.SMALL}
            onClick={() =>
              auditId
                ? openAuditBotModal({
                    auditId,
                  })
                : openModal(ModalFormId.RUN_AUDITBOT)
            }
            text="Test Controls"
            isDisabled={!canStartAuditRun}
            disabledReason="You do not have permission to test controls."
          />
        </FlexColumn>
      }
    />
  )
}

export const AuditBotIndexPageButtons = (): JSX.Element => {
  return <></>
}
